import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink, generatePath } from 'react-router';
import { makeStyles } from 'tss-react/mui';

import { SIZES } from '../../../../assets/constants/constants';
import ActionButton from '../../../../components/ActionButton';
import Section from '../../../../components/Section';
import { LANG } from '../../../../services/Config';

const useStyles = makeStyles()(({ breakpoints, spacing, typography }) => ({
  articleSectionsContainer: {
    maxWidth: SIZES.layout.width.sm,
    display: 'flex',
    flexDirection: 'column',
    [breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  articleSection: {
    maxWidth: SIZES.layout.width.sm,
  },
  title: {
    marginBottom: spacing(3),
    [breakpoints.up('sm')]: {
      marginBottom: spacing(4),
    },
  },
  subtitle: {
    marginBottom: spacing(2),
  },
  text: {
    marginBottom: spacing(4),
    [breakpoints.up('sm')]: {
      marginBottom: spacing(7),
    },
  },
  link: {
    // @ts-expect-error
    ...typography.p3,
  },
  list: {
    // @ts-expect-error
    ...typography.p3,
    paddingLeft: spacing(3.5),
  },
  openChatTitle: {
    maxWidth: SIZES.layout.width.sm,
    margin: spacing(7, 0, 4),
    textAlign: 'center',
  },
}));

const RefinancingArticle = () => {
  const { classes } = useStyles();
  const { routes } = LANG;

  return (
    <>
      <Section>
        <div className={classes.articleSection}>
          <Typography
            variant="h2"
            color="primary"
            className={classes.title}
            component="h1"
          >
            Refinansiering
          </Typography>
          <Typography
            variant="subtitle1Bold"
            component="h3"
            className={classes.subtitle}
          >
            Refinansiering handler om å samle smålån i et gunstigere felleslån.
            Men hva betyr det egentlig, og hvordan gjør du det? Vi guider deg
            gjennom det du trenger å vite om refinansiering.
          </Typography>
          <Typography variant="subtitle2" component="p">
            Har du mer enn ett lån? Da er du ikke alene. Boliglån og billån er
            vanlig, og i tillegg har vi gjerne forbrukslån og kredittkort.
            Ulempen med denne typen smålån er at de som regel er dyre. Da er det
            lurt å se på en refinansiering – det kan nemlig være tusener å spare
            hver måned. Men la oss først ta et dypdykk i hva refinansiering er.
          </Typography>
        </div>
      </Section>

      <Section background="paper">
        <div className={classes.articleSectionsContainer}>
          <Box
            pr={{ xs: 0, sm: 3 }}
            width={{ xs: '100%', sm: '50%' }}
            className={classes.articleSection}
          >
            {/* 1 */}
            <Typography
              variant="subtitle1Bold"
              component="h3"
              className={classes.subtitle}
            >
              Hva er refinansiering?
            </Typography>
            <Typography variant="p3" className={classes.text} component="p">
              Enkelt forklart er refinansiering å samle mange og dyre smålån med
              høy rente, til ett større og billigere lån med lavere rente. Disse
              smålånene kan være forbrukslån, kredittkortgjeld og handlekontoer.
            </Typography>

            {/* 2 */}
            <Typography
              variant="subtitle1Bold"
              component="h3"
              className={classes.subtitle}
            >
              Refinansiere forbrukslån?
            </Typography>
            <Typography variant="p3" className={classes.text} component="p">
              Har du mange forbrukslån, vil det sannsynligvis lønne seg å
              refinansiere. Du bør likevel vite at ikke alle får lavere rente,
              fordi det blant annet avhenger av økonomien og betalingsevnen din.
              Når det er sagt, får de aller fleste lavere rente enn de har på
              smålånene sine.
              <br />
              <br />
              Synes du det er vanskelig å orientere deg i jungelen av banker som
              tilbyr refinansiering av forbrukslån? Det er her{' '}
              <Link
                component={RouterLink}
                to={routes.HOME}
                className={classes.link}
              >
                Scoopr
              </Link>{' '}
              kommer inn i bildet. For det er enkelt å sjekke om du kan få
              lavere rente med en refinansiering, og det er enkelt å bytte
              avtale.
            </Typography>

            {/* 3 */}
            <Typography
              variant="subtitle1Bold"
              component="h3"
              className={classes.subtitle}
            >
              Hvordan refinansiere forbrukslån?
            </Typography>
            <Typography variant="p3" component="p">
              Scoopr gjør nemlig jobben for deg – altså sender søknaden din til
              flere banker. For å{' '}
              <Link
                component={RouterLink}
                to={generatePath(routes.PRODUCTS_FAQ_ARTICLE, {
                  product: 'refinansiering',
                  articleName: 'hvordan-soker-jeg-pa-refinansiering-hos-scoopr',
                })}
                className={classes.link}
              >
                refinansiere forbrukslån hos Scoopr
              </Link>
              , må du oppfylle noen minstekrav.
            </Typography>
            <ul className={classes.list}>
              <li>Du må være over 18 år</li>
              <li>Du må ha et norsk personnummer</li>
              <li>
                Du må kunne vise til en årlig inntekt på minst 140.000 kroner
                før skatt
              </li>
              <li>Du kan ikke ha noen betalingsanmerkninger</li>
            </ul>
            <Typography variant="p3" className={classes.text}>
              Hvis du har alt dette på plass, kan du gå videre for å søke om
              refinansiering.
            </Typography>
          </Box>

          <Box
            pl={{ xs: 0, sm: 3 }}
            width={{ xs: '100%', sm: '50%' }}
            className={classes.articleSection}
          >
            {/* 4 */}
            <Typography
              variant="subtitle1Bold"
              component="h3"
              className={classes.subtitle}
            >
              Hva betyr refinansiering?
            </Typography>
            <Typography variant="p3" component="p">
              Refinansiering betyr at du får en bedre deal på ett lån enn du får
              på mange smålån. Med andre ord kan du kutte ned på beløpet du
              betaler hver måned, og spare penger. Når du refinansierer, får du
              flere fordeler:
            </Typography>
            <ul className={classes.list}>
              <li>Du kan få lavere rente</li>
              <li>Du får færre gebyrer</li>
              <li>Du får én bank å forholde deg til</li>
              <li>Du får én månedlig regning</li>
              <li>Du unngår unødvendige kostnader</li>
              <li>Du får bedre oversikt</li>
              <li>Du får lavere totale kostnader</li>
              <li>Du kan begynne å spare penger til en buffer</li>
            </ul>
            <Typography variant="p3" className={classes.text} component="p">
              Alt i alt – med refinansiering trenger du ikke å betale mer enn du
              må.
            </Typography>

            {/* 5 */}
            <Typography
              variant="subtitle1Bold"
              component="h3"
              className={classes.subtitle}
            >
              Hvordan søke refinansiering?
            </Typography>
            <Typography variant="p3" className={classes.text} component="p">
              Hos Scoopr kan du søke om refinansiering fra 20.000 til 500.000
              kroner. Det er enkelt, gratis og uforpliktende, og tar deg kun to
              minutter.
              <br />
              <br />
              <Link
                component={RouterLink}
                to={generatePath(routes.PRODUCTS_FAQ_ARTICLE, {
                  product: 'refinansiering',
                  articleName: 'hvordan-soker-jeg-pa-refinansiering-hos-scoopr',
                })}
                className={classes.link}
              >
                Du søker refinansiering her
              </Link>
              . Legg inn opplysningene dine, og så blir søknaden automatisk
              sendt til 13 banker. De gjør en individuell vurdering av
              betalingsevnen din der de blant annet sjekker inntekt,
              betjeningsevne, kredittverdighet, om du allerede har gjeld og hvor
              høy den er.
              <br />
              <br />
              Som regel får du svar på søknaden din etter kort tid. Hvis
              søknaden blir godkjent trenger du ikke å gjøre noe mer –{' '}
              <Link
                component={RouterLink}
                to={generatePath(routes.PRODUCTS_FAQ_ARTICLE, {
                  product: 'refinansiering',
                  articleName: 'betaler-dere-ut-min-eksisterende-gjeld',
                })}
                className={classes.link}
              >
                da betaler normalt banken den gamle gjelden din
              </Link>
              . Du sparer både tid og arbeid, og i tillegg kan du være sikker på
              at den gamle og dyre gjelden din faktisk blir nedbetalt.
            </Typography>
          </Box>
        </div>

        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography
            variant="subtitle1Bold"
            component="h3"
            className={classes.openChatTitle}
          >
            Sjekk hvor mye du kan spare på refinansiering hos Scoopr i dag.
          </Typography>
          <ActionButton
            label="Søk refinansiering hos Scoopr"
            flow="debt_check"
            step="skip_start_from_refinansiering"
            color="secondary"
          />
        </Box>
      </Section>
    </>
  );
};

export default RefinancingArticle;
